import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhotoAnnotator } from "@/components/PhotoAnnotator";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { DemoOwner } from "@/components/demo/DemoOwner";
import { DemoWorker } from "@/components/demo/DemoWorker";
import { DemoJobs } from "@/components/demo/DemoJobs";
import { DEMO_JOB, DEMO_NEW_ITEM_SAMPLE, freshItems, urlToFile } from "@/lib/demoData";
import {
  Sparkles, RotateCcw, X, ArrowRight, Camera, Image as ImageIcon, PartyPopper, ClipboardCheck, Send, Check, FolderOpen, MapPin, Plus,
  MessageSquare, Mail, Copy, MoreHorizontal, Globe, Link2, FileText, QrCode, Eye, ShieldCheck,
} from "lucide-react";

const TOUR = {
  jobs: {
    label: "Your jobs",
    tip: "This is your home screen. Open the sample job to see the whole flow — or tap “New Job” to build your own from scratch.",
    icon: FolderOpen,
  },
  owner: {
    label: "You're the owner",
    tip: "Build your punch list. Tap the ✓ to verify an item, flag an issue, or add a new one. When it's ready, hit “Share” — your phone's share sheet opens, just like the app.",
    icon: ClipboardCheck,
  },
  worker: {
    label: "Now you're the crew",
    tip: "This is what your crew gets — the exact page your link opens, no login. Tap “Take proof”, mark up the photo, then “Send proof back” to finish.",
    icon: Camera,
  },
  verify: {
    label: "Back to you — verify",
    tip: "The proof's back. This is your job: orange means “proof in, waiting on you”. Tap the ✓ to verify each item — that's the whole loop.",
    icon: ShieldCheck,
  },
};
const STEP_ORDER = ["jobs", "owner", "worker"];

export default function Demo() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("jobs");
  const [job, setJob] = useState(DEMO_JOB);
  const [items, setItems] = useState(freshItems);
  const [newJobOpen, setNewJobOpen] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", location: "" });
  const [annot, setAnnot] = useState(null); // { files, mode, itemId, startLocked, title }
  const [picker, setPicker] = useState(null); // { for: "new-item" } | { for: "proof", itemId, suggested }
  const [shareOpen, setShareOpen] = useState(false); // legacy (unused) — kept to avoid churn
  const [sendSheetOpen, setSendSheetOpen] = useState(false);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [nativeShareOpen, setNativeShareOpen] = useState(false);
  const [sendLang, setSendLang] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [loadingSample, setLoadingSample] = useState(false);

  const goSignup = () => navigate("/auth?mode=signup");
  const restart = () => { setItems(freshItems()); setJob(DEMO_JOB); setPhase("jobs"); setAnnot(null); setPicker(null); toast.success("Demo reset"); };
  const openConcrete = () => { setJob(DEMO_JOB); setItems(freshItems()); setPhase("owner"); window.scrollTo({ top: 0 }); };
  const submitNewJob = () => {
    const name = newForm.name.trim() || "My First Job";
    setJob({ name, location: newForm.location.trim() });
    setItems([]);
    setNewJobOpen(false);
    setNewForm({ name: "", location: "" });
    setPhase("owner");
    window.scrollTo({ top: 0 });
  };

  /* ---------- send (mirrors the app: Send sheet → one link / split by trade / PDF; all simulated) ---------- */
  const shareDemo = () => setSendSheetOpen(true);
  const continueToCrew = () => {
    setSendSheetOpen(false); setShareSheetOpen(false); setDispatchOpen(false); setNativeShareOpen(false); setShareOpen(false);
    toast("👇 That's the link your crew gets", { description: "Now flip to their side — open it, add proof, and send it back." });
    setPhase("worker");
    window.scrollTo({ top: 0 });
  };
  const goVerify = () => {
    setItems((arr) => arr.map((it) => (it.proof ? { ...it, status: "sub_done" } : it)));
    setPhase("verify");
    toast("📸 Proof's back!", { description: "This is your job — orange means it's waiting on you. Tap the ✓ to verify." });
    window.scrollTo({ top: 0 });
  };

  /* ---------- owner actions ---------- */
  const verify = (item) => setItems((arr) => arr.map((it) => (it.id === item.id ? { ...it, status: it.status === "done" ? "pending" : "done" } : it)));
  const flag = (item) => setItems((arr) => arr.map((it) => (it.id === item.id ? { ...it, status: it.status === "issue" ? "pending" : "issue" } : it)));

  /* ---------- worker actions ---------- */
  const toggleWorker = (item) => setItems((arr) => arr.map((it) => (it.id === item.id ? { ...it, status: (it.status === "done" || it.status === "sub_done") ? "pending" : "done" } : it)));

  /* ---------- photo add flow (shared) ---------- */
  const openProofPicker = (item) => setPicker({ for: "proof", itemId: item.id, suggested: item.suggestedProof });
  const openNewItemPicker = () => setPicker({ for: "new-item" });

  const startAnnot = async (fileOrPromise) => {
    setPicker(null);
    const file = await fileOrPromise;
    if (!file) return;
    if (picker?.for === "proof") {
      setAnnot({ files: [file], mode: "proof", itemId: picker.itemId, startLocked: true, title: "Add proof" });
    } else {
      setAnnot({ files: [file], mode: "new-item", startLocked: false, title: "Mark reference" });
    }
  };

  const useSample = async () => {
    const url = picker?.for === "proof" ? picker.suggested : DEMO_NEW_ITEM_SAMPLE;
    setLoadingSample(true);
    try { await startAnnot(urlToFile(url, "sample.jpg")); }
    catch { toast.error("Couldn't load the sample photo"); setPicker(null); }
    finally { setLoadingSample(false); }
  };
  const pickFromDevice = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) startAnnot(Promise.resolve(f));
    else setPicker(null);
  };
  // Real camera capture only makes sense on a phone; on a computer we pop a hint instead.
  const isMobile = typeof window !== "undefined" && ((window.matchMedia && window.matchMedia("(pointer: coarse)").matches) || /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || ""));
  const showMobileOnly = () => toast("📱 Camera is a phone feature", { description: "Open this demo on your phone to snap a real proof photo. On a computer, use “Choose from Gallery” or a sample photo." });

  const handleAnnotSave = useCallback(async (blob, note) => {
    const url = URL.createObjectURL(blob);
    setAnnot((a) => {
      if (a?.mode === "proof") {
        setItems((arr) => arr.map((it) => (it.id === a.itemId ? { ...it, proof: { url, caption: note || "" }, status: phase === "worker" ? "done" : it.status } : it)));
      } else {
        const title = (note || "").trim() || "New punch item";
        setItems((arr) => [...arr, { id: `d${Date.now()}`, title, status: "pending", ref: url, suggestedProof: url, proof: null, description: "" }]);
        toast.success("Item added");
      }
      return a;
    });
  }, [phase]);

  const tour = TOUR[phase];

  return (
    <div className="min-h-[100dvh] bg-background pt-[52px]" data-testid="demo-page">
      {/* Fixed demo bar */}
      <div className="fixed inset-x-0 top-0 z-50 flex h-[52px] items-center justify-between gap-2 border-b border-emerald-500/30 bg-[hsl(222,47%,7%)]/95 px-3 backdrop-blur-xl sm:px-6" data-testid="demo-bar">
        <div className="flex min-w-0 items-center gap-1.5 text-emerald-300">
          <Sparkles className="h-4 w-4 shrink-0" />
          <span className="truncate text-[12px] font-bold uppercase tracking-wide sm:text-[13px]">Interactive demo · nothing is saved</span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button onClick={restart} data-testid="demo-restart" className="flex h-8 items-center gap-1 rounded-full border border-white/15 px-2.5 text-[12px] font-semibold text-white/85 transition-colors hover:bg-white/10">
            <RotateCcw className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Restart</span>
          </button>
          <Button onClick={goSignup} data-testid="demo-signup" size="sm" className="h-8 gap-1 bg-emerald-500 px-3 text-[12px] font-bold hover:bg-emerald-600">
            Sign up free <ArrowRight className="h-3.5 w-3.5" />
          </Button>
          <button onClick={() => navigate("/")} data-testid="demo-exit" aria-label="Exit demo" className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Narrator */}
      {phase !== "done" && tour && (
        <div className="mx-auto w-full max-w-md md:max-w-2xl px-4 pt-4 sm:px-8" data-testid="demo-narrator">
          <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-3.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white">
              <tour.icon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-500">Step {STEP_ORDER.indexOf(phase) + 1} of 3 · {tour.label}</div>
              <p className="mt-0.5 text-[13px] leading-snug text-foreground">{tour.tip}</p>
            </div>
          </div>
        </div>
      )}

      {/* Acts */}
      {phase === "jobs" && (
        <DemoJobs job={DEMO_JOB} onOpenJob={openConcrete} onNewJob={() => setNewJobOpen(true)} />
      )}

      {phase === "owner" && (
        <DemoOwner
          job={job}
          items={items}
          onVerify={verify}
          onFlag={flag}
          onAddItem={openNewItemPicker}
          onAddProof={openProofPicker}
          onOpenPhoto={(url) => setLightbox({ photos: [{ url, label: "Photo" }], index: 0 })}
          onShare={shareDemo}
          onBack={() => { setPhase("jobs"); window.scrollTo({ top: 0 }); }}
          sendLang={sendLang}
          onSendLang={setSendLang}
        />
      )}

      {phase === "verify" && (
        <DemoOwner
          job={job}
          items={items}
          mode="verify"
          onVerify={verify}
          onFlag={flag}
          onAddItem={openNewItemPicker}
          onAddProof={openProofPicker}
          onOpenPhoto={(url) => setLightbox({ photos: [{ url, label: "Photo" }], index: 0 })}
          onBack={() => { setPhase("worker"); window.scrollTo({ top: 0 }); }}
          onFinish={() => { setPhase("done"); window.scrollTo({ top: 0 }); }}
        />
      )}

      {phase === "worker" && (
        <DemoWorker
          job={job}
          items={items}
          onToggle={toggleWorker}
          onTakeProof={openProofPicker}
          onOpenPhoto={(url) => setLightbox({ photos: [{ url, label: "Photo" }], index: 0 })}
          onSend={goVerify}
        />
      )}

      {phase === "done" && (
        <div className="flex flex-col items-center justify-center px-8 pb-20 pt-16 text-center" data-testid="demo-done">
          <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-emerald-500/20">
            <span className="absolute -inset-2 animate-ping rounded-full border-2 border-emerald-500/30" />
            <PartyPopper className="h-12 w-12 text-emerald-400" />
          </div>
          <h2 className="mt-6 font-heading text-2xl font-bold">That's the whole loop.</h2>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            You built a list, sent it, got photo proof back, and verified it — without a single site visit. In the real app, the sender gets an instant notification the second proof lands.
          </p>
          <div className="mt-6 flex w-full max-w-xs flex-col gap-2.5">
            <Button onClick={goSignup} data-testid="demo-done-signup" className="h-12 w-full gap-2 bg-emerald-500 text-base font-bold hover:bg-emerald-600">
              Start free — 15-day trial <ArrowRight className="h-4 w-4" />
            </Button>
            <button onClick={restart} data-testid="demo-done-restart" className="flex h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:text-foreground">
              <RotateCcw className="h-4 w-4" /> Run the demo again
            </button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">No credit card required · $9.99/mo after trial · Cancel anytime</p>
        </div>
      )}

      {/* Add-photo source sheet */}
      <Sheet open={!!picker} onOpenChange={(o) => { if (!o) setPicker(null); }}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1rem)]" data-testid="demo-photo-sheet">
          <SheetHeader><SheetTitle className="text-center">{picker?.for === "proof" ? "Add proof photo" : "Add a new item"}</SheetTitle></SheetHeader>
          <p className="mb-2 mt-1 text-center text-xs text-muted-foreground">Try the built-in markup — draw an arrow, circle or ✕ on the photo.</p>
          <div className="mt-2 divide-y divide-border">
            <button data-testid="demo-use-sample" disabled={loadingSample} onClick={useSample} className="flex w-full items-center gap-3 py-4 text-left transition-colors active:bg-muted disabled:opacity-60">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500"><Sparkles className="h-5 w-5" /></span>
              <span className="flex-1"><span className="block font-semibold">Use a sample photo</span><span className="block text-xs text-muted-foreground">Fastest way to see it work</span></span>
            </button>
            {isMobile ? (
              <label data-testid="demo-take-photo" className="flex w-full cursor-pointer items-center gap-3 py-4 text-left transition-colors active:bg-muted">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500"><Camera className="h-5 w-5" /></span>
                <span className="flex-1"><span className="block font-semibold">Take Photo</span><span className="block text-xs text-muted-foreground">Use your camera — stays on your device</span></span>
                <input type="file" accept="image/*" capture="environment" hidden onChange={pickFromDevice} />
              </label>
            ) : (
              <button data-testid="demo-take-photo" onClick={showMobileOnly} className="flex w-full items-center gap-3 py-4 text-left transition-colors active:bg-muted">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500"><Camera className="h-5 w-5" /></span>
                <span className="flex-1"><span className="block font-semibold">Take Photo</span><span className="block text-xs text-muted-foreground">Use your phone's camera</span></span>
              </button>
            )}
            <label data-testid="demo-upload-photo" className="flex w-full cursor-pointer items-center gap-3 py-4 text-left transition-colors active:bg-muted">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500"><ImageIcon className="h-5 w-5" /></span>
              <span className="flex-1"><span className="block font-semibold">Choose from Gallery</span><span className="block text-xs text-muted-foreground">Import existing — stays on your device</span></span>
              <input type="file" accept="image/*" hidden onChange={pickFromDevice} />
            </label>
          </div>
          <Button variant="outline" className="mt-3 h-11 w-full" onClick={() => setPicker(null)} data-testid="demo-photo-cancel">Cancel</Button>
        </SheetContent>
      </Sheet>

      {/* Send sheet — mirrors the app: choose how the proof link goes out */}
      <Sheet open={sendSheetOpen} onOpenChange={setSendSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1rem)]" data-testid="demo-send-sheet">
          <SheetHeader><SheetTitle className="text-center">Send this list</SheetTitle></SheetHeader>
          <p className="mb-3 text-center text-xs text-muted-foreground">Choose how the proof link goes out — just like the app.</p>
          <div className="space-y-3">
            <button onClick={() => { setSendSheetOpen(false); setShareSheetOpen(true); }} data-testid="demo-send-link" className="flex w-full items-center gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-4 text-left transition-colors hover:bg-emerald-500/10">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white"><Send className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1"><div className="font-semibold">Send one link</div><div className="text-xs text-muted-foreground">Text the whole list to one person — simplest</div></div>
            </button>
            <button onClick={() => { setSendSheetOpen(false); setDispatchOpen(true); }} data-testid="demo-send-trade" className="flex w-full items-center gap-3 rounded-2xl border border-border p-4 text-left transition-colors hover:bg-muted">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600/15 text-blue-500"><Sparkles className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1"><div className="font-semibold">Split by trade</div><div className="text-xs text-muted-foreground">We sort the items — send each trade only theirs</div></div>
            </button>
            <button onClick={() => { setSendSheetOpen(false); toast("📄 PDF ready", { description: "In the real app this opens a polished PDF via your share sheet." }); continueToCrew(); }} data-testid="demo-send-pdf" className="flex w-full items-center gap-3 rounded-2xl border border-border p-4 text-left transition-colors hover:bg-muted">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-rose-500/15 text-rose-500"><FileText className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1"><div className="font-semibold">Send as PDF</div><div className="text-xs text-muted-foreground">A polished PDF via your phone's share sheet</div></div>
            </button>
          </div>
          <Button variant="outline" className="mt-4 h-11 w-full" onClick={() => setSendSheetOpen(false)} data-testid="demo-send-cancel">Cancel</Button>
        </SheetContent>
      </Sheet>

      {/* Share sheet (Send one link) — simulated, mirrors the app; nothing actually sends */}
      <Sheet open={shareSheetOpen} onOpenChange={setShareSheetOpen}>
        <SheetContent side="bottom" className="max-h-[92dvh] overflow-y-auto rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1rem)]" data-testid="demo-share-sheet">
          <SheetHeader><SheetTitle>Share this proof</SheetTitle></SheetHeader>
          <div className="mt-4 flex justify-center">
            <div className="flex h-[140px] w-[140px] items-center justify-center rounded-xl border border-slate-200 bg-white"><QrCode className="h-24 w-24 text-slate-900" /></div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Input readOnly value="send-proof.com/r/demo" data-testid="demo-share-url" className="h-11 bg-background text-xs" />
            <Button size="icon" onClick={() => toast("Demo only — nothing actually sends")} className="h-11 w-11 shrink-0 bg-emerald-500 hover:bg-emerald-600"><Copy className="h-4 w-4" /></Button>
          </div>
          <Button onClick={() => setNativeShareOpen(true)} data-testid="demo-share-via" className="mt-3 h-11 w-full gap-1 bg-emerald-500 hover:bg-emerald-600"><Send className="h-4 w-4" /> Share via…</Button>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Button variant="outline" onClick={() => toast("Demo only")} className="h-11 gap-1"><Link2 className="h-4 w-4" /> Copy</Button>
            <Button variant="outline" onClick={() => toast("Demo only")} className="h-11 gap-1"><MessageSquare className="h-4 w-4" /> SMS</Button>
            <Button variant="outline" onClick={() => toast("Demo only")} className="h-11 gap-1"><Mail className="h-4 w-4" /> Email</Button>
          </div>
          <div className="mt-5 rounded-xl border border-border bg-background p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold"><Eye className="h-4 w-4 text-emerald-500" /> Open tracking</div>
              <span className="font-heading text-2xl font-bold">0</span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">You'll see the moment your crew opens it — live.</div>
          </div>
          <Button onClick={continueToCrew} data-testid="demo-share-continue" className="mt-4 h-12 w-full gap-1 bg-emerald-500 text-base font-bold hover:bg-emerald-600">See your crew's side <ArrowRight className="h-4 w-4" /></Button>
          <p className="mt-2 text-center text-xs text-muted-foreground">Demo only — nothing actually sends.</p>
        </SheetContent>
      </Sheet>

      {/* Native phone share popup — the OS share sheet the real app triggers (simulated) */}
      <Sheet open={nativeShareOpen} onOpenChange={setNativeShareOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1rem)]" data-testid="demo-native-share-sheet">
          <SheetHeader><SheetTitle className="text-center">Share your list</SheetTitle></SheetHeader>
          <div className="mt-2 flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-500"><Send className="h-5 w-5" /></span>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">Punch list — {job.name}</div>
              <div className="truncate text-xs text-muted-foreground">send-proof.com/r/demo</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-4 gap-3 text-center">
            {[
              { Icon: MessageSquare, label: "Messages", cls: "bg-emerald-500 text-white" },
              { Icon: Mail, label: "Mail", cls: "bg-sky-500 text-white" },
              { Icon: Copy, label: "Copy", cls: "bg-neutral-700 text-white" },
              { Icon: MoreHorizontal, label: "More", cls: "bg-neutral-700 text-white" },
            ].map(({ Icon, label, cls }) => (
              <button key={label} data-testid={`demo-native-share-${label.toLowerCase()}`} onClick={() => { setNativeShareOpen(false); continueToCrew(); }} className="flex flex-col items-center gap-1.5 transition-transform active:scale-95">
                <span className={`flex h-14 w-14 items-center justify-center rounded-2xl ${cls}`}><Icon className="h-6 w-6" /></span>
                <span className="text-xs text-muted-foreground">{label}</span>
              </button>
            ))}
          </div>
          <p className="mt-4 text-center text-xs text-muted-foreground">Your phone's share sheet — demo only, nothing actually sends. Pick any option to see your crew's side.</p>
        </SheetContent>
      </Sheet>

      {/* Split-by-trade sheet — simulated Sort & Send */}
      <Sheet open={dispatchOpen} onOpenChange={(o) => { if (!o) continueToCrew(); }}>
        <SheetContent side="bottom" className="max-h-[92dvh] overflow-y-auto rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1rem)]" data-testid="demo-dispatch-sheet">
          <SheetHeader><SheetTitle>Sort &amp; Send by trade</SheetTitle></SheetHeader>
          <p className="mb-3 mt-1 text-center text-xs text-muted-foreground">We sorted these automatically — send each trade only theirs.</p>
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="demo-bucket-concrete">
            <div className="flex items-center justify-between gap-2">
              <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-sm font-semibold text-emerald-400">Concrete / Flatwork</span>
              <span className="text-xs text-muted-foreground">{items.length} item{items.length === 1 ? "" : "s"}</span>
            </div>
            <ul className="mt-3 space-y-1.5 text-sm">
              {items.map((it) => (<li key={it.id} className="truncate text-muted-foreground">• {it.title}</li>))}
            </ul>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button onClick={() => toast("Demo only — nothing actually sends")} className="h-11 gap-1 bg-emerald-500 hover:bg-emerald-600"><Send className="h-4 w-4" /> Share</Button>
              <Button variant="outline" onClick={() => toast("Demo only")} className="h-11 gap-1"><FileText className="h-4 w-4" /> PDF</Button>
            </div>
          </div>
          <Button onClick={continueToCrew} data-testid="demo-dispatch-continue" className="mt-4 h-12 w-full gap-1 bg-emerald-500 text-base font-bold hover:bg-emerald-600">See your crew's side <ArrowRight className="h-4 w-4" /></Button>
          <p className="mt-2 text-center text-xs text-muted-foreground">Demo only — nothing actually sends.</p>
        </SheetContent>
      </Sheet>

      <Sheet open={newJobOpen} onOpenChange={setNewJobOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1rem)]" data-testid="demo-newjob-sheet">
          <SheetHeader><SheetTitle>New Job</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-4">
            <div>
              <Label htmlFor="demo-jn">Job name</Label>
              <Input id="demo-jn" data-testid="demo-newjob-name" autoFocus value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submitNewJob()} placeholder="e.g. Unit 4B Turn" className="mt-1 h-11" />
            </div>
            <div>
              <Label htmlFor="demo-jl">Location</Label>
              <Input id="demo-jl" data-testid="demo-newjob-location" value={newForm.location} onChange={(e) => setNewForm({ ...newForm, location: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submitNewJob()} placeholder="Address or site" className="mt-1 h-11" />
            </div>
            <Button data-testid="demo-newjob-create" onClick={submitNewJob} className="h-11 w-full gap-1 bg-emerald-500 hover:bg-emerald-600"><Plus className="h-4 w-4" /> Create Job</Button>
          </div>
        </SheetContent>
      </Sheet>

      {lightbox && <PhotoLightbox data={lightbox} onClose={() => setLightbox(null)} testId="demo-lightbox" />}
      {annot && (
        <PhotoAnnotator
          files={annot.files}
          title={annot.title}
          startLocked={annot.startLocked}
          onSaveEach={handleAnnotSave}
          onDone={() => setAnnot(null)}
          onCancel={() => setAnnot(null)}
        />
      )}
    </div>
  );
}
